/**
 * Users-tab state and actions for the Supplier Detail page: the user pop-up,
 * its site permissions, Edit / Reset Password and Login As.
 *
 * Split out of {@link useSupplierDetail} to keep that hook under the 500-line
 * file limit; behaviour is unchanged apart from CR-7745 (Login As failures now
 * reach the user as a toast instead of the console only).
 */

import { useState } from 'react';
import { toast } from 'sonner';
import {
  authAPI,
  coreAPI,
  SupplierProfile,
  UserProfile,
  UserSitePermission,
} from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import { getApiErrorMessage } from '../../utils/errorUtils';

interface SupplierUserActionsOptions {
  /** The supplier whose Users tab is open (null while it loads). */
  supplier: SupplierProfile | null;
  /** Re-fetches the Users tab list after an edit. */
  fetchUsers: () => Promise<void>;
  /** Login As is offered to Helix admins and customer users only. */
  canImpersonate: boolean;
}

/**
 * Owns the Users-tab pop-up, permission toggles, edit/reset-password dialogs
 * and Login As for one supplier.
 *
 * @param options - the supplier, the list refetch and the Login As gate
 * @returns the state and handlers spread into {@link useSupplierDetail}'s result
 * @example
 * const userActions = useSupplierUserActions({ supplier, fetchUsers, canImpersonate });
 */
export function useSupplierUserActions({
  supplier,
  fetchUsers,
  canImpersonate,
}: SupplierUserActionsOptions) {
  const { startImpersonation } = useAuth();

  // User modal state
  const [selectedUser, setSelectedUser] = useState<UserProfile | null>(null);
  const [userModalOpen, setUserModalOpen] = useState(false);
  const [userModalTab, setUserModalTab] = useState<'profile' | 'sites'>('profile');
  const [userSitePermissions, setUserSitePermissions] = useState<UserSitePermission[]>([]);
  const [permissionsLoading, setPermissionsLoading] = useState(false);
  const [savingPermission, setSavingPermission] = useState<number | null>(null);
  const [impersonating, setImpersonating] = useState<number | null>(null);

  // CR-5857: Edit + Reset Password dialog state, owned at the page level so
  // both the cards in UsersTab and the action bar in UserModal can trigger
  // the same flow without duplicating it.
  const [editUser, setEditUser] = useState<UserProfile | null>(null);
  const [editUserOpen, setEditUserOpen] = useState(false);
  const [resettingPasswordFor, setResettingPasswordFor] = useState<number | null>(null);

  const handleOpenUserModal = async (userProfile: UserProfile) => {
    setSelectedUser(userProfile);
    setUserModalTab('profile');
    setUserModalOpen(true);

    try {
      setPermissionsLoading(true);
      // CR-5344: Pass the supplier company id so the popup shows sites
      // scoped to THIS supplier's group (not the user's home group).
      // Multi-company users (Helix admins, COOs etc.) would otherwise
      // leak their other-tenant site memberships into this dialog.
      const response = await coreAPI.getUserSitePermissions(
        userProfile.id,
        supplier?.company ?? undefined
      );
      setUserSitePermissions(response.data);
    } catch (err: unknown) {
      console.error('Error fetching user permissions:', err);
      setUserSitePermissions([]);
    } finally {
      setPermissionsLoading(false);
    }
  };

  const handleCloseUserModal = () => {
    setUserModalOpen(false);
    setSelectedUser(null);
    setUserSitePermissions([]);
    setUserModalTab('profile');
  };

  const handleToggleSiteLink = async (siteId: number, isLinked: boolean) => {
    if (!selectedUser) return;

    setSavingPermission(siteId);
    try {
      const response = await coreAPI.updateUserSitePermission(selectedUser.id, siteId, {
        is_linked: isLinked,
        is_active: isLinked,
        can_purchasing: isLinked,
        can_quality: isLinked,
        can_finance: isLinked,
      });
      setUserSitePermissions((prev) =>
        prev.map((p) => (p.site === siteId ? response.data : p))
      );
    } catch (err: unknown) {
      console.error('Error updating site permission:', err);
    } finally {
      setSavingPermission(null);
    }
  };

  const handleUpdatePermission = async (siteId: number, field: string, value: boolean) => {
    if (!selectedUser) return;

    setSavingPermission(siteId);
    try {
      const response = await coreAPI.updateUserSitePermission(selectedUser.id, siteId, {
        [field]: value,
      });
      setUserSitePermissions((prev) =>
        prev.map((p) => (p.site === siteId ? response.data : p))
      );
    } catch (err: unknown) {
      console.error('Error updating permission:', err);
    } finally {
      setSavingPermission(null);
    }
  };

  const handleOpenEditUser = (userProfile: UserProfile) => {
    setEditUser(userProfile);
    setEditUserOpen(true);
  };

  const handleCloseEditUser = () => {
    setEditUserOpen(false);
    setEditUser(null);
  };

  const handleEditUserSaved = async () => {
    // CR-5857: refresh the list so the updated name/email/role propagate to
    // the cards immediately, and refresh the modal copy if it's still open
    // on the same user.
    await fetchUsers();
    if (selectedUser && editUser && selectedUser.id === editUser.id) {
      try {
        const refreshed = await coreAPI.getUserProfile(editUser.id);
        setSelectedUser(refreshed.data);
      } catch (err: unknown) {
        console.error('Error refreshing edited user:', err);
      }
    }
  };

  const handleResetPassword = async (userProfile: UserProfile) => {
    if (resettingPasswordFor === userProfile.id) return;
    try {
      setResettingPasswordFor(userProfile.id);
      const res = await coreAPI.adminResetUserPassword(userProfile.id);
      toast.success(res.data.message || `Password reset email sent to ${userProfile.user.email}`);
    } catch (err: unknown) {
      // CR-5857: same wrapped shape as EditUserDialog — message first, with
      // older keys as defensive fallbacks.
      const axiosErr = err as {
        response?: { data?: { error?: string; detail?: string; message?: string } };
      };
      const message =
        axiosErr.response?.data?.message ||
        axiosErr.response?.data?.error ||
        axiosErr.response?.data?.detail ||
        (err instanceof Error ? err.message : 'Failed to send password reset email');
      toast.error(message);
    } finally {
      setResettingPasswordFor(null);
    }
  };

  const handleImpersonate = async (userProfile: UserProfile) => {
    if (!canImpersonate) return;

    try {
      setImpersonating(userProfile.user.id);
      // CR-4858 follow-up: pass view_company_id to the backend so it can
      // pin profile.current_company / current_site to the same company
      // the admin clicked through. Without this the backend would default
      // to the target user's home — which for multi-company users is
      // rarely what the admin intended (see CR-4858 description).
      const response = await authAPI.impersonate(
        userProfile.user.id,
        undefined,
        supplier?.company,
      );

      // API returns standardized format: { status, message, data: { target_user, target_token, original_token } }
      const impersonationData = response.data.data;

      // CR-4858: Pin the post-impersonation landing scope to *this* supplier
      // (the company the admin clicked through), not the impersonated user's
      // primary company. Multi-company users would otherwise land on whichever
      // company is stamped as their primary, which is rarely what the admin
      // intended when triaging from a specific supplier's Users tab.
      const contextCompany = supplier
        ? { id: supplier.company, name: supplier.company_name }
        : undefined;

      // Start impersonation (triggers full page reload)
      startImpersonation(
        impersonationData.target_user,
        impersonationData.target_token,
        impersonationData.original_token,
        contextCompany,
        impersonationData.audit_log_id,
      );
    } catch (err: unknown) {
      // CR-7745: a refused Login As used to log to the console only, so the
      // button looked dead. Surface the server's reason, as User Management does.
      console.error('Error impersonating user:', err);
      toast.error(getApiErrorMessage(err, 'Failed to start Login As session'));
    } finally {
      setImpersonating(null);
    }
  };

  return {
    selectedUser,
    userModalOpen,
    userModalTab,
    userSitePermissions,
    permissionsLoading,
    savingPermission,
    impersonating,
    editUser,
    editUserOpen,
    resettingPasswordFor,
    setUserModalTab,
    handleOpenUserModal,
    handleCloseUserModal,
    handleToggleSiteLink,
    handleUpdatePermission,
    handleOpenEditUser,
    handleCloseEditUser,
    handleEditUserSaved,
    handleResetPassword,
    handleImpersonate,
  };
}
